import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, ShoppingCart, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RecoveryCartCard — abandoned-cart recovery funnel for SuperAdmin.
 * Reads GET /api/payments/recovery-stats (staff only) and shows the
 * nudge→purchase funnel plus a simple bar chart, so the 15-min vs 24-h
 * timing can be tuned with real numbers.
 */
const RecoveryCartCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/payments/recovery-stats`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load recovery stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const chart = data ? [
    { name: 'Nudged', value: data.nudged_total || 0, fill: '#f59e0b' },
    { name: 'Stage 2 sent', value: data.stage2_sent || 0, fill: '#fb923c' },
    { name: 'Recovered', value: data.recovered_total || 0, fill: '#10b981' },
  ] : [];

  return (
    <Card className="border-2 border-emerald-300/50 dark:border-emerald-700/40" data-testid="recovery-cart-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
            <ShoppingCart className="w-5 h-5" />
            Abandoned-Cart Recovery
          </span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="recovery-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 dark:text-red-400 py-4">{error}</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="text-2xl font-bold" data-testid="recovery-nudged">{data.nudged_total}</div>
                <div className="text-xs text-muted-foreground">Carts nudged</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-600" data-testid="recovery-recovered">{data.recovered_total}</div>
                <div className="text-xs text-muted-foreground">Recovered</div>
              </div>
              <div className="rounded-lg bg-muted/50 p-3 text-center">
                <div className="text-2xl font-bold" data-testid="recovery-rate">{data.recovery_rate_pct}%</div>
                <div className="text-xs text-muted-foreground">Recovery rate</div>
              </div>
            </div>

            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chart} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chart.map((e, i) => <Cell key={i} fill={e.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span>Stage-1 pending: <b className="text-foreground">{data.stage1_pending}</b></span>
              <span>Recovered @15min: <b className="text-foreground">{data.recovered_stage1}</b></span>
              <span>Recovered @24h: <b className="text-foreground">{data.recovered_stage2}</b></span>
              <span>Paid w/ promo: <b className="text-foreground">{data.paid_with_promo}</b></span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecoveryCartCard;
